import { useMemo, useState } from "react";
import Layout from "@/components/feature/Layout";
import CharacterGuide from "@/components/feature/CharacterGuide";
import { mobilePlans, type MobilePlan } from "@/data/plans.generated";

const carrierGroups: Record<string, string[]> = {
  major: ["ドコモ", "au", "ソフトバンク"],
  sub: ["UQ mobile", "Y!mobile"],
  online: ["ahamo", "povo", "LINEMO"],
  mvno: ["IIJmio", "日本通信SIM", "mineo", "イオンモバイル"],
  rakuten: ["楽天モバイル"],
};

const filters = [
  { key: "major", label: "大手キャリア" }, { key: "sub", label: "サブブランド" },
  { key: "online", label: "オンライン専用" },
  { key: "mvno", label: "格安SIM" }, { key: "rakuten", label: "楽天モバイル" },
];

const yen = (value: number) => `${value.toLocaleString()}円`;
const capacity = (plan: MobilePlan) => plan.gb === null ? "無制限" : `${plan.gb}GB`;
const call = (plan: MobilePlan) => {
  if (plan.included) return `${plan.includedMinutes || plan.shortMinutes || 5}分通話込み`;
  if (plan.long === 0) return "指定アプリで国内通話無料";
  if (plan.short === 0) return "短時間通話込み";
  return `短時間 +${yen(plan.short)} / かけ放題 +${yen(plan.long)}`;
};

const fullShopSupport = new Set(["ドコモ", "au", "ソフトバンク", "楽天モバイル", "UQ mobile", "Y!mobile", "イオンモバイル"]);
const supportSummary = (plan: MobilePlan) => {
  if (fullShopSupport.has(plan.brand)) return "店舗相談あり";
  if (plan.brand === "ahamo") return "店頭支援あり（有料）";
  if (plan.brand === "mineo" || plan.shop) return "一部店舗で相談可";
  return "オンライン中心";
};

export default function Compare() {
  const [filter, setFilter] = useState("major");
  const verifiedPlans = useMemo(() => mobilePlans.filter((plan) => plan.status === "verified"), []);
  const list = verifiedPlans.filter((plan) => carrierGroups[filter]?.includes(plan.brand));
  const brands = useMemo(() => {
    const unique = new Map<string, MobilePlan>();
    list.forEach((plan) => { if (!unique.has(plan.brand)) unique.set(plan.brand, plan); });
    return [...unique.values()];
  }, [list]);

  return (
    <Layout>
      <section className="border-b border-background-300 bg-background-200">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6 py-10 md:py-14">
          <div className="text-xs text-secondary-600 font-semibold tracking-widest">通信会社を比較</div>
          <h1 className="mt-2 text-2xl md:text-3xl font-bold text-foreground-900">料金プランを、同じ条件で比較</h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-7 max-w-3xl">各社の主な料金プランを、通常月額・データ量・通話・サポートで横並びにしました。割引や端末代を混ぜずに比較できます。</p>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-4 md:px-6 py-10">
        <div className="flex flex-wrap gap-2">
          {filters.map((item) => (
            <button key={item.key} onClick={() => setFilter(item.key)} className={`px-4 py-2 rounded-full text-sm font-medium border cursor-pointer whitespace-nowrap ${filter === item.key ? "bg-primary-500 text-white border-primary-500" : "bg-background-50 text-foreground-700 border-background-300 hover:border-primary-500"}`}>{item.label}</button>
          ))}
        </div>
        <p className="mt-3 text-xs leading-5 text-foreground-500">サポートは「店舗相談あり」「店頭支援あり（有料）」「一部店舗で相談可」「オンライン中心」の4区分で表示しています。</p>

        <div className="mt-6 hidden md:block overflow-x-auto rounded-lg border border-background-300 bg-background-50">
          <table className="min-w-[1000px] w-full text-sm">
            <thead className="bg-background-100 text-foreground-700 text-left"><tr>
              <th className="px-4 py-3 font-semibold">通信会社</th><th className="px-4 py-3 font-semibold">プラン</th><th className="px-4 py-3 font-semibold">通常月額</th><th className="px-4 py-3 font-semibold">データ</th><th className="px-4 py-3 font-semibold">通話</th><th className="px-4 py-3 font-semibold">サポート</th><th className="px-4 py-3 font-semibold">確認日</th>
            </tr></thead>
            <tbody>{list.map((plan) => (
              <tr key={plan.id} className="border-t border-background-200 align-top">
                <td className="px-4 py-4 font-semibold text-foreground-900">{plan.brand}</td>
                <td className="px-4 py-4"><a href={plan.url} target="_blank" rel="noreferrer" className="text-primary-500 hover:underline">{plan.name}</a></td>
                <td className="px-4 py-4 text-foreground-900">{yen(plan.base)}</td><td className="px-4 py-4 text-foreground-700">{capacity(plan)}</td><td className="px-4 py-4 text-foreground-700">{call(plan)}</td><td className="px-4 py-4 text-foreground-700 whitespace-nowrap">{supportSummary(plan)}</td><td className="px-4 py-4 text-foreground-600 whitespace-nowrap">{plan.checked}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>

        <div className="mt-6 md:hidden space-y-3">{list.map((plan) => (
          <div key={plan.id} className="rounded-lg border border-background-300 bg-background-50 p-5">
            <div className="flex items-start justify-between gap-3"><div><div className="text-[15px] font-bold text-foreground-900">{plan.brand}</div><div className="text-xs text-foreground-500">{plan.name}</div></div><div className="text-right"><div className="text-[11px] text-foreground-500">通常月額</div><div className="text-sm font-bold text-foreground-900">{yen(plan.base)}</div></div></div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs"><div className="p-2 bg-background-100 rounded"><div className="text-foreground-500">データ</div><div className="text-foreground-900 mt-0.5">{capacity(plan)}</div></div><div className="p-2 bg-background-100 rounded"><div className="text-foreground-500">通話</div><div className="text-foreground-900 mt-0.5">{call(plan)}</div></div><div className="p-2 bg-background-100 rounded col-span-2"><div className="text-foreground-500">相談</div><div className="font-semibold text-foreground-900 mt-0.5">{supportSummary(plan)}</div></div></div>
            <a href={plan.url} target="_blank" rel="noreferrer" className="mt-3 inline-block text-xs font-semibold text-primary-500">公式情報を確認 <i className="ri-external-link-line" /></a>
          </div>
        ))}</div>

        <div className="mt-14 space-y-6">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900">各社の確認ポイント</h2>
          <CharacterGuide size="sm" message="表示額は通常料金です。申し込み前に、対応端末・利用地域・最新料金を公式ページで確認してください。" />
          {brands.map((plan) => (
            <div key={plan.brand} className="rounded-lg border border-background-300 bg-background-50 p-6">
              <div className="flex items-start justify-between flex-wrap gap-3"><div><div className="text-lg font-bold text-foreground-900">{plan.brand}</div><div className="text-xs text-foreground-500 mt-0.5">掲載 {list.filter((item) => item.brand === plan.brand).length}プラン ・ 確認日 {plan.checked}</div></div><a href={plan.url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-primary-500">公式料金ページ <i className="ri-external-link-line" /></a></div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm leading-6"><div><div className="text-xs font-semibold text-primary-500 mb-2">相談・サポート</div><div className="font-semibold text-foreground-900">{supportSummary(plan)}</div><p className="mt-1 text-xs text-foreground-600">{plan.support}</p></div><div><div className="text-xs font-semibold text-accent-600 mb-2">初期費用など</div><p className="text-foreground-700">{plan.fees}</p></div></div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-xs text-foreground-500 leading-6">※ 通信料のみの比較です。端末代、期間限定の割引・還元、ユニバーサルサービス料等は含みません。料金プラン全体の最終確認日：2026年9月20日。契約前に必ず公式サイトの最新条件をご確認ください。</p>
      </section>
    </Layout>
  );
}
